import { BufferGeometry, type Color, Float32BufferAttribute, Matrix3, Matrix4, Uint16BufferAttribute, Vector3 } from 'three';
import type { Pcg32 } from '../../sim/rng';

/** Per-face colours for a box; anything missing falls back to `all`. */
export interface BoxColors {
  all: Color;
  top?: Color;
  bottom?: Color;
  front?: Color; // +z, the side the camera sees
  back?: Color;
  left?: Color; // -x
  right?: Color;
}

const _a = new Vector3();
const _b = new Vector3();
const _c = new Vector3();
const _n = new Vector3();
const _e1 = new Vector3();
const _e2 = new Vector3();
const _nm = new Matrix3();

/** Translation + yaw + optional pitch/roll + scale, the transform nearly every part needs. */
export function place(x = 0, y = 0, z = 0, yaw = 0, scale = 1, pitch = 0, roll = 0): Matrix4 {
  const m = new Matrix4().makeTranslation(x, y, z);
  if (yaw !== 0) m.multiply(new Matrix4().makeRotationY(yaw));
  if (pitch !== 0) m.multiply(new Matrix4().makeRotationX(pitch));
  if (roll !== 0) m.multiply(new Matrix4().makeRotationZ(roll));
  if (scale !== 1) m.multiply(new Matrix4().makeScale(scale, scale, scale));
  return m;
}

/**
 * Accumulates flat-shaded, vertex-coloured triangles (05 §5). Every face gets its own
 * vertices and an exact face normal, so lighting bands are crisp and no `flatShading`
 * derivative tricks are needed at low resolution. No UVs, no textures, no indices.
 */
export class MeshBuilder {
  private readonly positions: number[] = [];
  private readonly normals: number[] = [];
  private readonly colors: number[] = [];
  private readonly skinIndices: number[] = [];
  private readonly stack: Matrix4[] = [new Matrix4()];
  private bone = 0;
  private skinned = false;

  get triangleCount(): number {
    return this.positions.length / 9;
  }

  /** Run `fn` with `m` applied on top of the current transform. */
  with(m: Matrix4, fn: () => void): this {
    const top = this.stack[this.stack.length - 1] ?? new Matrix4();
    this.stack.push(top.clone().multiply(m));
    fn();
    this.stack.pop();
    return this;
  }

  /** Subsequent geometry is rigidly bound to this bone index (characters only). */
  setBone(index: number): this {
    this.bone = index;
    this.skinned = true;
    return this;
  }

  tri(a: Vector3, b: Vector3, c: Vector3, color: Color): this {
    const m = this.stack[this.stack.length - 1];
    _a.copy(a);
    _b.copy(b);
    _c.copy(c);
    if (m) {
      _a.applyMatrix4(m);
      _b.applyMatrix4(m);
      _c.applyMatrix4(m);
    }
    _e1.subVectors(_b, _a);
    _e2.subVectors(_c, _a);
    _n.crossVectors(_e1, _e2);
    if (_n.lengthSq() < 1e-12) return this;
    _n.normalize();
    for (const v of [_a, _b, _c]) {
      this.positions.push(v.x, v.y, v.z);
      this.normals.push(_n.x, _n.y, _n.z);
      this.colors.push(color.r, color.g, color.b);
      this.skinIndices.push(this.bone, 0, 0, 0);
    }
    return this;
  }

  /**
   * A triangle with its own vertex normals, for smooth (cel-shaded) curves: the toon bands then
   * follow the surface instead of breaking on every facet.
   */
  triSmooth(a: Vector3, b: Vector3, c: Vector3, na: Vector3, nb: Vector3, nc: Vector3, color: Color): this {
    const m = this.stack[this.stack.length - 1] ?? new Matrix4();
    _nm.getNormalMatrix(m);
    const put = (p: Vector3, n: Vector3): void => {
      _a.copy(p).applyMatrix4(m);
      _n.copy(n).applyMatrix3(_nm).normalize();
      this.positions.push(_a.x, _a.y, _a.z);
      this.normals.push(_n.x, _n.y, _n.z);
      this.colors.push(color.r, color.g, color.b);
      this.skinIndices.push(this.bone, 0, 0, 0);
    };
    put(a, na);
    put(b, nb);
    put(c, nc);
    return this;
  }

  /** Quad with corners in counter-clockwise order as seen from outside. */
  quad(a: Vector3, b: Vector3, c: Vector3, d: Vector3, color: Color): this {
    return this.tri(a, b, c, color).tri(a, c, d, color);
  }

  /** Axis-aligned box centred on x/z, sitting on y = 0 unless `centerY` is set. */
  box(w: number, h: number, d: number, colors: BoxColors | Color, m?: Matrix4, centerY = false): this {
    const col: BoxColors = 'all' in colors ? colors : { all: colors };
    const x = w / 2;
    const z = d / 2;
    const y0 = centerY ? -h / 2 : 0;
    const y1 = y0 + h;
    const build = (): void => {
      const p = (px: number, py: number, pz: number): Vector3 => new Vector3(px, py, pz);
      this.quad(p(-x, y1, z), p(x, y1, z), p(x, y1, -z), p(-x, y1, -z), col.top ?? col.all);
      this.quad(p(-x, y0, -z), p(x, y0, -z), p(x, y0, z), p(-x, y0, z), col.bottom ?? col.all);
      this.quad(p(-x, y0, z), p(x, y0, z), p(x, y1, z), p(-x, y1, z), col.front ?? col.all);
      this.quad(p(x, y0, -z), p(-x, y0, -z), p(-x, y1, -z), p(x, y1, -z), col.back ?? col.all);
      this.quad(p(-x, y0, -z), p(-x, y0, z), p(-x, y1, z), p(-x, y1, -z), col.left ?? col.all);
      this.quad(p(x, y0, z), p(x, y0, -z), p(x, y1, -z), p(x, y1, z), col.right ?? col.all);
    };
    if (m) this.with(m, build);
    else build();
    return this;
  }

  /** N-sided tapered cylinder on y = 0. `rTop = 0` makes a cone. */
  cylinder(
    rBottom: number,
    rTop: number,
    h: number,
    sides: number,
    color: Color,
    m?: Matrix4,
    capColor?: Color,
  ): this {
    const build = (): void => {
      const top = new Vector3(0, h, 0);
      const bottom = new Vector3(0, 0, 0);
      for (let i = 0; i < sides; i++) {
        const a0 = (i / sides) * Math.PI * 2;
        const a1 = ((i + 1) / sides) * Math.PI * 2;
        const b0 = new Vector3(Math.sin(a0) * rBottom, 0, Math.cos(a0) * rBottom);
        const b1 = new Vector3(Math.sin(a1) * rBottom, 0, Math.cos(a1) * rBottom);
        const t0 = new Vector3(Math.sin(a0) * rTop, h, Math.cos(a0) * rTop);
        const t1 = new Vector3(Math.sin(a1) * rTop, h, Math.cos(a1) * rTop);
        if (rTop > 1e-6) {
          this.quad(b0, b1, t1, t0, color);
          this.tri(top, t0, t1, capColor ?? color);
        } else {
          this.tri(b0, b1, top, color);
        }
        this.tri(bottom, b1, b0, capColor ?? color);
      }
    };
    if (m) this.with(m, build);
    else build();
    return this;
  }

  /** Triangular prism: a gable roof or a tent. Ridge runs along x, base on y = 0. */
  gable(w: number, h: number, d: number, color: Color, m?: Matrix4, endColor?: Color): this {
    const build = (): void => {
      const x = w / 2;
      const z = d / 2;
      const p = (px: number, py: number, pz: number): Vector3 => new Vector3(px, py, pz);
      this.quad(p(-x, 0, z), p(x, 0, z), p(x, h, 0), p(-x, h, 0), color);
      this.quad(p(x, 0, -z), p(-x, 0, -z), p(-x, h, 0), p(x, h, 0), color);
      this.tri(p(-x, 0, -z), p(-x, 0, z), p(-x, h, 0), endColor ?? color);
      this.tri(p(x, 0, z), p(x, 0, -z), p(x, h, 0), endColor ?? color);
    };
    if (m) this.with(m, build);
    else build();
    return this;
  }

  /**
   * Ball built from a subdivided octahedron: bodies, heads, eyes, boulders. `jitter` (0..0.5)
   * pushes vertices in and out; pass an rng for seeded variety. `smooth` (the default) gives
   * each vertex the ellipsoid's own normal, so it shades as a curve; `smooth: false` is faceted.
   */
  blob(
    rx: number,
    ry: number,
    rz: number,
    color: Color,
    m?: Matrix4,
    opts: {
      jitter?: number; rng?: Pcg32; detail?: 0 | 1 | 2 | 3; topColor?: Color; flatBottom?: boolean; smooth?: boolean;
    } = {},
  ): this {
    const { jitter = 0, rng, detail = 1, topColor, flatBottom = false, smooth = true } = opts;
    const base: Vector3[] = [
      new Vector3(1, 0, 0),
      new Vector3(-1, 0, 0),
      new Vector3(0, 1, 0),
      new Vector3(0, -1, 0),
      new Vector3(0, 0, 1),
      new Vector3(0, 0, -1),
    ];
    const v = (i: number): Vector3 => base[i] ?? new Vector3();
    let faces: Array<[Vector3, Vector3, Vector3]> = [
      [v(4), v(0), v(2)],
      [v(0), v(5), v(2)],
      [v(5), v(1), v(2)],
      [v(1), v(4), v(2)],
      [v(0), v(4), v(3)],
      [v(5), v(0), v(3)],
      [v(1), v(5), v(3)],
      [v(4), v(1), v(3)],
    ];
    for (let d = 0; d < detail; d++) {
      const next: Array<[Vector3, Vector3, Vector3]> = [];
      for (const [a, b, c] of faces) {
        const ab = a.clone().add(b).normalize();
        const bc = b.clone().add(c).normalize();
        const ca = c.clone().add(a).normalize();
        next.push([a, ab, ca], [ab, b, bc], [ca, bc, c], [ab, bc, ca]);
      }
      faces = next;
    }
    // Jitter must be identical for shared vertices, so key it by quantised direction.
    const offsets = new Map<string, number>();
    const shape = (dir: Vector3): Vector3 => {
      const key = `${Math.round(dir.x * 100)},${Math.round(dir.y * 100)},${Math.round(dir.z * 100)}`;
      let k = offsets.get(key);
      if (k === undefined) {
        k = 1 + (rng ? rng.range(-jitter, jitter) : 0);
        offsets.set(key, k);
      }
      const y = flatBottom && dir.y < 0 ? dir.y * 0.15 : dir.y;
      return new Vector3(dir.x * rx * k, y * ry * k, dir.z * rz * k);
    };
    // The ellipsoid's gradient at a shaped point: its smooth normal.
    const normal = (p: Vector3, dir: Vector3): Vector3 => {
      const ey = flatBottom && dir.y < 0 ? ry * 0.15 : ry;
      return new Vector3(p.x / (rx * rx), p.y / (ey * ey), p.z / (rz * rz)).normalize();
    };
    const build = (): void => {
      for (const [a, b, c] of faces) {
        const upward = (a.y + b.y + c.y) / 3 > 0.35;
        const col = upward && topColor ? topColor : color;
        const pa = shape(a);
        const pb = shape(b);
        const pc = shape(c);
        if (smooth) this.triSmooth(pa, pb, pc, normal(pa, a), normal(pb, b), normal(pc, c), col);
        else this.tri(pa, pb, pc, col);
      }
    };
    if (m) this.with(m, build);
    else build();
    return this;
  }

  /** Append another builder's triangles under a transform (instancing on the CPU). */
  merge(other: MeshBuilder, m: Matrix4): this {
    const p = other.positions;
    const c = other.colors;
    return this.with(m, () => {
      for (let i = 0; i < p.length; i += 9) {
        const col = { r: c[i] ?? 0, g: c[i + 1] ?? 0, b: c[i + 2] ?? 0 } as Color;
        this.tri(
          new Vector3(p[i], p[i + 1], p[i + 2]),
          new Vector3(p[i + 3], p[i + 4], p[i + 5]),
          new Vector3(p[i + 6], p[i + 7], p[i + 8]),
          col,
        );
      }
    });
  }

  build(): BufferGeometry {
    const g = new BufferGeometry();
    g.setAttribute('position', new Float32BufferAttribute(this.positions, 3));
    g.setAttribute('normal', new Float32BufferAttribute(this.normals, 3));
    g.setAttribute('color', new Float32BufferAttribute(this.colors, 3));
    if (this.skinned) {
      g.setAttribute('skinIndex', new Uint16BufferAttribute(this.skinIndices, 4));
      const weights = new Float32Array((this.skinIndices.length / 4) * 4);
      for (let i = 0; i < weights.length; i += 4) weights[i] = 1;
      g.setAttribute('skinWeight', new Float32BufferAttribute(weights, 4));
    }
    g.computeBoundingSphere();
    g.computeBoundingBox();
    return g;
  }
}
